import React, { useEffect, useState } from 'react';
import { apiGet, apiPost, apiPut, apiDel } from '../../lib/api';
import { toast } from 'sonner';
import {
  UserCheck, UserX, LogIn, LogOut, Plus, RefreshCw,
  Users, Clock, CheckCircle2, XCircle,
} from 'lucide-react';

const STATUS_BADGE = {
  expected:    'bg-teal-100 text-teal-800',
  checked_in:  'bg-emerald-100 text-emerald-800',
  checked_out: 'bg-slate-100 text-slate-600',
  cancelled:   'bg-red-100 text-red-600',
};

const EMPTY_FORM = {
  visitor_name: '', company: '', phone: '', host_name: '',
  purpose: '', expected_date: '', id_proof_type: 'Aadhaar',
  id_proof_no: '', vehicle_no: '', remarks: '',
};

export default function AdminVisitors() {
  const [visitors, setVisitors] = useState([]);
  const [tab, setTab] = useState('today');
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [checkinId, setCheckinId] = useState(null);
  const [badgeNo, setBadgeNo] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      if (tab === 'today') {
        const d = await apiGet('/admin/visitors/today');
        setVisitors(d.visitors || []);
      } else if (tab === 'active') {
        const d = await apiGet('/admin/visitors/active');
        setVisitors(d.visitors || []);
      } else {
        const d = await apiGet(`/admin/visitors?date=${today}`);
        setVisitors(d.visitors || []);
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [tab]); // eslint-disable-line

  const handlePreRegister = async (e) => {
    e.preventDefault();
    try {
      await apiPost('/admin/visitors/pre-register', form);
      toast.success('Visitor pre-registered');
      setShowForm(false);
      setForm(EMPTY_FORM);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const handleCheckin = async (vid) => {
    try {
      await apiPut(`/admin/visitors/${vid}/check-in`, { badge_no: badgeNo || undefined });
      toast.success('Visitor checked in');
      setCheckinId(null);
      setBadgeNo('');
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Check-in failed'); }
  };

  const handleCheckout = async (vid) => {
    if (!window.confirm('Mark this visitor as checked out?')) return;
    try {
      await apiPut(`/admin/visitors/${vid}/check-out`, {});
      toast.success('Visitor checked out');
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Check-out failed'); }
  };

  const handleCancel = async (vid) => {
    if (!window.confirm('Cancel this visit?')) return;
    try {
      await apiDel(`/admin/visitors/${vid}`);
      toast.success('Visit cancelled');
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Cancel failed'); }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div data-testid="admin-visitors" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E] mb-1">Admin · SOP A5</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Visitor Management</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="flex items-center gap-1.5 text-xs px-3 py-2 border border-slate-200 rounded-sm hover:bg-slate-50">
            <RefreshCw size={12} /> Refresh
          </button>
          <button
            onClick={() => { setShowForm(true); setForm({ ...EMPTY_FORM, expected_date: today }); }}
            className="flex items-center gap-1.5 text-xs px-3 py-2 bg-[#1F7A6E] text-white rounded-sm hover:bg-[#14504E]"
            data-testid="visitor-pre-register-btn"
          >
            <Plus size={12} /> Pre-register Visitor
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200">
        {[
          { key: 'today', label: "Today's Visitors", icon: Users },
          { key: 'active', label: 'Currently Inside', icon: UserCheck },
          { key: 'all', label: 'All Records', icon: Clock },
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-1.5 px-4 py-2 text-xs font-display font-bold uppercase tracking-wider border-b-2 transition-colors ${
              tab === key ? 'border-[#1F7A6E] text-[#1F7A6E]' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <Icon size={12} /> {label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-auto">
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-400">Loading…</div>
        ) : visitors.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-400">No visitors found</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {['Visitor', 'Company', 'Phone', 'Host', 'Purpose', 'Expected', 'In', 'Out', 'Badge', 'Status', 'Actions'].map(h => (
                  <th key={h} className="text-left px-3 py-2 font-display font-bold uppercase tracking-wider text-slate-500 text-[10px] whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {visitors.map(v => (
                <tr key={v.id} className="hover:bg-slate-50">
                  <td className="px-3 py-2 font-display font-semibold whitespace-nowrap">{v.visitor_name}</td>
                  <td className="px-3 py-2 text-slate-500">{v.company || '—'}</td>
                  <td className="px-3 py-2">{v.phone || '—'}</td>
                  <td className="px-3 py-2">{v.host_name}</td>
                  <td className="px-3 py-2 max-w-[140px] truncate">{v.purpose}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{v.expected_date}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-emerald-700">{v.in_time ? v.in_time.slice(11, 16) : '—'}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-slate-500">{v.out_time ? v.out_time.slice(11, 16) : '—'}</td>
                  <td className="px-3 py-2">{v.badge_no || '—'}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-sm ${STATUS_BADGE[v.status] || ''}`}>
                      {v.status?.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1 items-center">
                      {v.status === 'expected' && (
                        checkinId === v.id ? (
                          <div className="flex gap-1 items-center">
                            <input
                              placeholder="Badge #"
                              value={badgeNo}
                              onChange={e => setBadgeNo(e.target.value)}
                              className="w-20 border border-slate-200 rounded-sm px-1.5 py-0.5 text-xs"
                            />
                            <button onClick={() => handleCheckin(v.id)} className="text-emerald-700 hover:text-emerald-900" title="Confirm check-in" data-testid={`checkin-confirm-${v.id}`}>
                              <CheckCircle2 size={14} />
                            </button>
                            <button onClick={() => setCheckinId(null)} className="text-slate-400 hover:text-slate-600">
                              <XCircle size={14} />
                            </button>
                          </div>
                        ) : (
                          <button onClick={() => setCheckinId(v.id)} title="Check In" className="text-[#1F7A6E] hover:text-[#0F172A]" data-testid={`checkin-btn-${v.id}`}>
                            <LogIn size={14} />
                          </button>
                        )
                      )}
                      {v.status === 'checked_in' && (
                        <button onClick={() => handleCheckout(v.id)} title="Check Out" className="text-amber-600 hover:text-amber-800" data-testid={`checkout-btn-${v.id}`}>
                          <LogOut size={14} />
                        </button>
                      )}
                      {v.status === 'expected' && (
                        <button onClick={() => handleCancel(v.id)} title="Cancel visit" className="text-red-500 hover:text-red-700">
                          <UserX size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pre-register form */}
      {showForm && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-sm shadow-xl w-full max-w-lg p-6 space-y-4">
            <h2 className="font-display font-bold text-lg">Pre-register Visitor</h2>
            <form onSubmit={handlePreRegister} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Visitor Name *</label>
                  <input required value={form.visitor_name} onChange={e => setForm(f => ({ ...f, visitor_name: e.target.value }))}
                    className="w-full border border-slate-200 rounded-sm px-2.5 py-1.5 text-sm" data-testid="visitor-name-input" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Company</label>
                  <input value={form.company} onChange={e => setForm(f => ({ ...f, company: e.target.value }))}
                    className="w-full border border-slate-200 rounded-sm px-2.5 py-1.5 text-sm" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Phone</label>
                  <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                    className="w-full border border-slate-200 rounded-sm px-2.5 py-1.5 text-sm" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Host Name *</label>
                  <input required value={form.host_name} onChange={e => setForm(f => ({ ...f, host_name: e.target.value }))}
                    className="w-full border border-slate-200 rounded-sm px-2.5 py-1.5 text-sm" data-testid="host-name-input" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Expected Date *</label>
                  <input type="date" required value={form.expected_date} onChange={e => setForm(f => ({ ...f, expected_date: e.target.value }))}
                    className="w-full border border-slate-200 rounded-sm px-2.5 py-1.5 text-sm" data-testid="expected-date-input" />
                </div>
                <div className="col-span-2">
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Purpose *</label>
                  <input required value={form.purpose} onChange={e => setForm(f => ({ ...f, purpose: e.target.value }))}
                    className="w-full border border-slate-200 rounded-sm px-2.5 py-1.5 text-sm" data-testid="purpose-input" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">ID Proof Type</label>
                  <select value={form.id_proof_type} onChange={e => setForm(f => ({ ...f, id_proof_type: e.target.value }))}
                    className="w-full border border-slate-200 rounded-sm px-2.5 py-1.5 text-sm">
                    {['Aadhaar', 'PAN', 'Passport', 'Driving Licence', 'Voter ID', 'Other'].map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">ID Proof No.</label>
                  <input value={form.id_proof_no} onChange={e => setForm(f => ({ ...f, id_proof_no: e.target.value }))}
                    className="w-full border border-slate-200 rounded-sm px-2.5 py-1.5 text-sm" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Vehicle No.</label>
                  <input value={form.vehicle_no} onChange={e => setForm(f => ({ ...f, vehicle_no: e.target.value }))}
                    className="w-full border border-slate-200 rounded-sm px-2.5 py-1.5 text-sm" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Remarks</label>
                  <input value={form.remarks} onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))}
                    className="w-full border border-slate-200 rounded-sm px-2.5 py-1.5 text-sm" />
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <button type="submit" className="flex-1 bg-[#1F7A6E] text-white text-sm py-2 rounded-sm hover:bg-[#14504E] font-display font-bold" data-testid="visitor-submit-btn">
                  Pre-register
                </button>
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border border-slate-200 rounded-sm text-sm hover:bg-slate-50">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
